/**
 * SummaryPane — product key-data grid in the SUMMARY pane.
 *
 * Renders product attributes (building, floor, rooms, area, exposition, etc.)
 * as a two-column DataGrid. Field visibility is driven by real panelSettings keys
 * and capabilities — no fictional unit_sheet_summary block needed.
 * The registry in utils/summaryItems.js controls field list and formatting.
 */

import { useMemo } from 'react'

import { useApp }     from '../../../../app/AppContext.jsx'
import { useCatalog } from '../../../catalog/context/CatalogContext.jsx'
import { useProduct } from '../../index.js'

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { buildSummarySettings, buildSummaryItems } from '../../utils/summaryItems.js'

import { DataGrid } from '../../../../shared/ui/DataGrid/index.js'
import { StatusBadge } from '../../../../shared/ui/StatusBadge/index.js'

import styles from './SummaryPane.module.scss'

// ─────────────────────────────────────────────────────────────────────────────

export function SummaryPane() {
  // ── i18n ────────────────────────────────────────────────────────────────────
  const { t } = useI18n()

  // ── Context reads ────────────────────────────────────────────────────────────
  const { panelSettings, isMobile }           = useApp()
  const { buildings, compartments, tiers } = useCatalog()
  const { selection, capabilities } = useProduct()

  const product = selection.product

  // ── Effective settings ────────────────────────────────────────────────────
  const settings = useMemo(
    () => buildSummarySettings(panelSettings, capabilities, buildings),
    [panelSettings, capabilities, buildings],
  )

  // ── Items ─────────────────────────────────────────────────────────────────
  const items = useMemo(
    () => buildSummaryItems(product, settings, t, {
      compartmentTypes: compartments,
      panelSettings,
      tiers
    }),
    [product, settings, t, compartments, panelSettings],
  )

  // ── Render ───────────────────────────────────────────────────────────────
  if (!product || items.length === 0) return null

  return (
    <div className={styles.summaryPane}>
      <div className={styles.statusBar}>
        <StatusBadge status={product.status} size="normal" />
      </div>
      <DataGrid items={items} columns={isMobile ? 4 : 3} size="normal" />
    </div>
  )
}
